import type { ReactNode } from 'react';
import { CalendarDays, Clock3, ExternalLink, Trophy } from 'lucide-react';
import type { PublicCompetition } from '@tark/shared/lib';
import BrandMark from '../BrandMark';
import {
  formatCompetitionDate,
  formatCompetitionRange,
  formatEnum,
} from '../../pages/competitions/publicCompetitionUtils';

export function CompetitionPublicLayout({
  accountHref,
  accountLabel,
  children,
}: {
  accountHref: string;
  accountLabel: string;
  children: ReactNode;
}) {
  const marketingBaseUrl = import.meta.env.VITE_MARKETING_BASE_URL || '/';
  return (
    <div className="public-theme min-h-screen bg-background text-text">
      <header className="border-b border-border bg-surface/95">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <a href={marketingBaseUrl} className="inline-flex items-center" aria-label="TARK home">
            <BrandMark size="sm" titleClassName="text-lg" />
          </a>
          <a
            href={accountHref}
            className="text-sm font-semibold text-primary transition-colors hover:text-[color:var(--color-primary-hover)]"
          >
            {accountLabel}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6 sm:py-10">{children}</main>

      <footer className="border-t border-border bg-surface">
        <div className="mx-auto flex max-w-6xl flex-wrap gap-x-5 gap-y-2 px-4 py-5 text-xs text-text-muted sm:px-6">
          <a href="/privacy" className="hover:text-text">Privacy</a>
          <a href="/terms" className="hover:text-text">Terms</a>
          <a href="/data-request" className="hover:text-text">Data request</a>
          <span className="ml-auto">TARK competitions</span>
        </div>
      </footer>
    </div>
  );
}

export function CompetitionOverview({ competition }: { competition: PublicCompetition }) {
  const registrationCloses = formatCompetitionDate(
    competition.registrationClosesAt,
    competition.timezone,
    true
  );

  return (
    <section className="border-b border-border pb-7 sm:pb-9">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-semibold text-primary">
          {formatEnum(competition.registrationState)}
        </span>
        <span className="text-text-muted">{formatEnum(competition.sportCode)}</span>
      </div>

      <h1 className="mt-4 max-w-3xl text-3xl font-bold leading-tight text-text sm:text-4xl">
        {competition.name}
      </h1>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Fact icon={<CalendarDays className="h-4 w-4" />} label="Competition" value={formatCompetitionRange(competition)} />
        <Fact
          icon={<Clock3 className="h-4 w-4" />}
          label="Registration"
          value={registrationCloses ? `Closes ${registrationCloses}` : formatEnum(competition.registrationState)}
        />
        <Fact
          icon={<Trophy className="h-4 w-4" />}
          label="Prize"
          value={competition.prizeDescription || 'Play, results, and standings'}
        />
      </div>

      {competition.rulesUrl ? (
        <a
          href={competition.rulesUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          Competition rules
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : null}
    </section>
  );
}

function Fact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-h-20 items-start gap-3 rounded-lg border border-border bg-surface px-4 py-3">
      <span className="mt-0.5 text-primary">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-text-subtle">{label}</p>
        <p className="mt-1 text-sm font-semibold leading-snug text-text">{value}</p>
      </div>
    </div>
  );
}
